const allPublishingStates = new Map<string, GeckoPublishingState>()

/**
 * A representation of the publishing state of the data: either
 * Raw, Working, or Finalized
 */
export class GeckoPublishingState {
  //----------------------------------------------------------------------------

  public static readonly Description = 'Publishing state of data'

  //----------------------------------------------------------------------------

  public static readonly Raw = new GeckoPublishingState(
    'raw',
    'Pre-QA',
    "Data which has not yet been QA'd, not ready for customer viewing"
  )

  public static readonly Working = new GeckoPublishingState(
    'working',
    'QA',
    'Data in QA, not ready for customer viewing'
  )

  public static readonly Finalized = new GeckoPublishingState(
    'finalized',
    'Finalized',
    'Finalized data, visible to the customer'
  )

  public static readonly Archived = new GeckoPublishingState(
    'archived',
    'Archived',
    'Data which is no longer in the publishing pipeline'
  )

  //----------------------------------------------------------------------------

  /** A unique ID */
  public readonly id: string

  /** A user-facing name for this object, or its ID if none is populated */
  public readonly name: string

  /** A user-facing description for this specific publishing state */
  public readonly description: string

  // Private constructor ensures singleton pattern
  private constructor(id: string, name: string, description: string) {
    this.id = id
    this.name = name
    this.description = description
    allPublishingStates.set(id, this)
  }

  /**
   * A default sorting method for component data frame sources
   * @param a The first source object
   * @param b The second source object
   */
  public static DefaultSort(
    a: GeckoPublishingState,
    b: GeckoPublishingState
  ): number {
    return defaultSortOrder.indexOf(a.id) - defaultSortOrder.indexOf(b.id)
  }

  /**
   * Returns the unique data source instance for a given ID if it exists
   * @param id The unique ID of this data source
   */
  public static GetByID(
    id: string | undefined
  ): GeckoPublishingState | undefined {
    return allPublishingStates.get(id ?? '')
  }

  /**
   * Returns the unique data source instance for a given ID, or creates a
   * unique data source instance from scratch for this new ID
   * @param id The unique ID of this object
   */
  public static GetOrCreate(id: string): GeckoPublishingState {
    return (
      GeckoPublishingState.GetByID(id) ?? new GeckoPublishingState(id, id, '')
    )
  }
}

// Define default sort order based on IDs of the static instances
const defaultSortOrder = [
  GeckoPublishingState.Finalized.id,
  GeckoPublishingState.Working.id,
  GeckoPublishingState.Raw.id,
  GeckoPublishingState.Archived.id,
]
